import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const TestimonialsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: '5',
    service: '',
    feedback: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const testimonials = [
    {
      rating: 5,
      title: 'Excellent Service',
      text: 'Smart Heatplumb installed our new heating system quickly and professionally. The team was courteous, clean, and the quality of work exceeded our expectations. Highly recommend!',
      name: 'Michael O\'Sullivan',
      location: 'Killarney, Kerry',
      service: 'Heating Installation',
      date: 'January 2025',
      avatar: 'MO',
    },
    {
      rating: 5,
      title: 'Professional Team',
      text: 'Had an emergency plumbing issue on a Sunday evening. They responded within an hour and fixed the problem efficiently. Great service and fair pricing. Will definitely use again.',
      name: 'Sarah Murphy',
      location: 'Tralee, Kerry',
      service: 'Emergency Plumbing',
      date: 'December 2024',
      avatar: 'SM',
    },
    {
      rating: 5,
      title: 'Bathroom Renovation',
      text: 'Our bathroom looks amazing! The team handled everything from design to installation. They were punctual, professional, and cleaned up after themselves every day. Fantastic work!',
      name: 'Patrick Fitzgerald',
      location: 'Kenmare, Kerry',
      service: 'Bathroom Renovation',
      date: 'November 2024',
      avatar: 'PF',
    },
    {
      rating: 5,
      title: 'Heat Pump Installation',
      text: 'Very impressed with the heat pump installation. The technicians explained everything clearly and the system works perfectly. Energy bills have already decreased significantly.',
      name: 'Emma Collins',
      location: 'Killarney, Kerry',
      service: 'Heat Pump',
      date: 'October 2024',
      avatar: 'EC',
    },
    {
      rating: 5,
      title: 'Reliable & Trustworthy',
      text: 'Been using Smart Heatplumb for all our plumbing needs for over two years. Always reliable, professional, and reasonably priced. They\'re our go-to for any heating or plumbing work.',
      name: 'John McCarthy',
      location: 'Dingle, Kerry',
      service: 'General Plumbing',
      date: 'September 2024',
      avatar: 'JM',
    },
    {
      rating: 5,
      title: 'Underfloor Heating',
      text: 'The underfloor heating installation transformed our home. The process was smooth, the team was knowledgeable, and the results are incredible. Our house has never been more comfortable!',
      name: 'Claire O\'Brien',
      location: 'Killarney, Kerry',
      service: 'Underfloor Heating',
      date: 'August 2024',
      avatar: 'CO',
    },
    {
      rating: 5,
      title: 'Outstanding Work',
      text: 'Replaced all our radiators and the difference is remarkable. The new radiators heat up faster and look much better. Professional installation and great value for money.',
      name: 'David Walsh',
      location: 'Tralee, Kerry',
      service: 'Radiator Replacement',
      date: 'July 2024',
      avatar: 'DW',
    },
    {
      rating: 5,
      title: 'Highly Recommended',
      text: 'Called them for a boiler service and they found a potential issue that could have been serious. Fixed it on the spot and explained everything. Honest and professional service.',
      name: 'Mary O\'Connor',
      location: 'Killarney, Kerry',
      service: 'Boiler Service',
      date: 'June 2024',
      avatar: 'MC',
    },
    {
      rating: 5,
      title: 'Great Experience',
      text: 'From quote to completion, everything was handled professionally. The bathroom renovation was completed on time and within budget. Couldn\'t be happier with the results!',
      name: 'Tom Brennan',
      location: 'Kenmare, Kerry',
      service: 'Bathroom Renovation',
      date: 'May 2024',
      avatar: 'TB',
    },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('rating', formData.rating);
      formBody.append('service', formData.service);
      formBody.append('feedback', formData.feedback);

      const response = await fetch('https://readdy.ai/api/form/d58rhuuisj01hmefqvj0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          rating: '5',
          service: '',
          feedback: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-primary text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Customer Testimonials
              </h1>
              <p className="text-lg sm:text-xl text-gray-300 leading-relaxed">
                Read what our satisfied customers across Kerry have to say about our plumbing and heating services. Your feedback helps us maintain the highest standards.
              </p>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-accent mb-2">500+</div>
                <div className="text-gray-600 text-sm">Happy Customers</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-accent mb-2">4.9</div>
                <div className="text-gray-600 text-sm">Average Rating</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-accent mb-2">98%</div>
                <div className="text-gray-600 text-sm">Satisfaction Rate</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-accent mb-2">10+</div>
                <div className="text-gray-600 text-sm">Years Experience</div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">
                What Our Clients Say
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Real feedback from real customers who have experienced our professional service
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-shadow"
                >
                  {/* Stars */}
                  <div className="flex space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent text-lg"></i>
                    ))}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-primary mb-3">
                    {testimonial.title}
                  </h3>

                  {/* Review Text */}
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    {testimonial.text}
                  </p>

                  {/* Service Badge */}
                  <div className="inline-block px-3 py-1 bg-accent/10 text-accent text-xs font-semibold rounded-full mb-4 whitespace-nowrap">
                    {testimonial.service}
                  </div>

                  {/* User Info */}
                  <div className="flex items-center space-x-3 pt-6 border-t border-gray-100">
                    <div className="w-12 h-12 flex items-center justify-center bg-accent/10 rounded-full flex-shrink-0">
                      <span className="text-accent font-semibold text-sm">
                        {testimonial.avatar}
                      </span>
                    </div>
                    <div>
                      <p className="text-primary font-semibold text-sm">
                        {testimonial.name}
                      </p>
                      <p className="text-gray-500 text-xs">{testimonial.location}</p>
                      <p className="text-gray-400 text-xs">{testimonial.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feedback Form Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">
                Share Your Experience
              </h2>
              <p className="text-gray-600 text-lg">
                We'd love to hear about your experience with our services
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg">
              <form id="feedback-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent text-sm"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent text-sm"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="rating" className="block text-sm font-medium text-gray-700 mb-2">
                      Rating *
                    </label>
                    <select
                      id="rating"
                      name="rating"
                      value={formData.rating}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent text-sm"
                    >
                      <option value="5">⭐⭐⭐⭐⭐ Excellent</option>
                      <option value="4">⭐⭐⭐⭐ Very Good</option>
                      <option value="3">⭐⭐⭐ Good</option>
                      <option value="2">⭐⭐ Fair</option>
                      <option value="1">⭐ Poor</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                      Service Received *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent text-sm"
                    >
                      <option value="">Select a service</option>
                      <option value="Plumbing">Plumbing</option>
                      <option value="Heating Installation">Heating Installation</option>
                      <option value="Bathroom Renovation">Bathroom Renovation</option>
                      <option value="Heat Pump">Heat Pump</option>
                      <option value="Underfloor Heating">Underfloor Heating</option>
                      <option value="Emergency Service">Emergency Service</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Feedback * (Max 500 characters)
                  </label>
                  <textarea
                    id="feedback"
                    name="feedback"
                    value={formData.feedback}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your experience with our service..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.feedback.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-accent hover:bg-accent-dark text-white py-4 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Feedback</span>
                      <i className="ri-send-plane-line"></i>
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-check-line mr-2"></i>
                    Thank you for your feedback! We appreciate your time.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    Something went wrong. Please try again.
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TestimonialsPage;